import React, { useState, useRef, useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { createPortal } from 'react-dom'

/**
 * SectionedSelect — a dropdown with grouped options, rich rendering, and
 * single-or-multi selection.
 *
 * It's the verbose cousin of Select/MultiSelect: use it when you want the
 * picker to communicate MORE than "label → value", e.g. a hotel with its
 * location, star rating, and current usage; a status with a color swatch and
 * description; a template with a preview line.
 *
 * Section headers subdivide options by meaning (e.g. "Con allocazioni" vs.
 * "Vuoti") and carry an optional description shown at the top of the section.
 *
 * Options support:
 *   - `label`:    primary line (string or ReactNode)
 *   - `sublabel`: secondary line under the label (muted, 12px)
 *   - `meta`:     right-aligned badge/element (e.g. stars, counts)
 *   - `muted`:    render with desaturated background + muted text — still selectable
 *   - `disabled`: not selectable, cursor: not-allowed
 *
 * Usage:
 *   <SectionedSelect
 *     label="Hotel"
 *     multiple
 *     value={selected}              // string[] when multiple, string when single
 *     onChange={setSelected}        // same
 *     sections={[
 *       { label: 'Con allocazioni', options: [...] },
 *       { label: 'Vuoti', options: [...], description: '...' }
 *     ]}
 *   />
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  position: relative;
`

const Label = styled.label`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.muted};
`

const Trigger = styled.div`
  width: 100%;
  border: 1px solid ${({ theme, $isOpen }) => ($isOpen ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radii.md};
  min-height: 40px;
  padding: 6px 12px;
  background: white;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  box-shadow: ${({ theme, $isOpen }) => ($isOpen ? `0 0 0 3px ${theme.colors.primary}33` : 'none')};
  transition: all 0.2s ease;
  user-select: none;
`

const Arrow = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.muted};
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.2s ease;
  margin-left: 8px;
`

const OptionsList = styled.div`
  position: fixed;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  max-height: 420px;
  overflow-y: auto;
  z-index: 99999;
  padding: 4px;
  min-width: 280px;
`

const SearchRow = styled.div`
  padding: 6px 6px 8px 6px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 4px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 10px 6px 10px;
  margin-top: 4px;
  font-size: 10.5px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-top: 1px solid #f1f5f9;

  &:first-of-type {
    border-top: none;
    margin-top: 0;
  }
`

const SectionDescription = styled.div`
  padding: 0 10px 6px 10px;
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
`

const SectionCount = styled.span`
  background: #f1f5f9;
  color: #64748b;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
`

const SectionAction = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0;
  text-transform: none;
  &:hover { background: #f1f5f9; }
`

const Option = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: background 0.1s ease;

  ${({ $muted }) => $muted && css`
    background: #f8fafc;
  `}

  ${({ $selected, theme }) => $selected && css`
    background: ${theme.colors.primary}14;
  `}

  &:hover {
    background: ${({ $disabled, $selected, theme }) => (
      $disabled ? 'inherit' :
      $selected ? `${theme.colors.primary}22` : '#f1f5f9'
    )};
  }
`

const Checkbox = styled.input`
  margin: 3px 0 0 0;
  cursor: inherit;
  accent-color: ${({ theme }) => theme.colors.primary};
`

const OptionBody = styled.div`
  flex: 1;
  min-width: 0;
`

const OptionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $muted }) => ($muted ? '#64748b' : '#1e293b')};
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const OptionSublabel = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
  line-height: 1.3;
`

const OptionMeta = styled.div`
  flex-shrink: 0;
  font-size: 11px;
  color: #64748b;
  margin-left: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`

const Tag = styled.span`
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
`

const SelectedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const EmptyState = styled.div`
  padding: 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 12px;
`

export function SectionedSelect({
  label,
  sections = [],
  value,
  onChange,
  multiple = false,
  disabled,
  placeholder = 'Seleziona...',
  showTags = true,
  searchable = true,
  style,
  className
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const optionsRef = useRef(null)
  const searchInputRef = useRef(null)

  // Normalize value to always work as array internally, preserve the caller's
  // single-or-multi contract on the way out.
  const selectedValues = useMemo(() => {
    if (multiple) return Array.isArray(value) ? value : []
    return value ? [value] : []
  }, [multiple, value])

  const flatOptions = useMemo(() => {
    const out = []
    for (const sec of sections) {
      for (const opt of (sec.options || [])) out.push(opt)
    }
    return out
  }, [sections])

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current && !containerRef.current.contains(event.target) &&
        (!optionsRef.current || !optionsRef.current.contains(event.target))
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
    if (!isOpen) setSearchTerm('')
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    function handleResize() { setIsOpen(false) }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen])

  const handleOpen = () => {
    if (disabled) return
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownMaxHeight = 428
      const openAbove = spaceBelow < dropdownMaxHeight && rect.top > spaceBelow
      if (openAbove) {
        setDropdownPos({
          bottom: window.innerHeight - rect.top + 4, top: 'auto',
          left: rect.left, width: Math.max(rect.width, 320), openAbove: true
        })
      } else {
        setDropdownPos({
          top: rect.bottom + 4, bottom: 'auto',
          left: rect.left, width: Math.max(rect.width, 320), openAbove: false
        })
      }
    }
    setIsOpen(!isOpen)
  }

  const toggleValue = (val, optionDisabled) => {
    if (disabled || optionDisabled) return
    if (multiple) {
      const next = selectedValues.includes(val)
        ? selectedValues.filter(v => v !== val)
        : [...selectedValues, val]
      onChange && onChange(next)
    } else {
      onChange && onChange(val === value ? '' : val)
      setIsOpen(false)
    }
  }

  // Match options against the search term. An option matches if its label or
  // sublabel (when both are strings) contains the term case-insensitively.
  const matchesSearch = (opt) => {
    if (!searchTerm) return true
    const q = searchTerm.toLowerCase()
    const hit = (x) => typeof x === 'string' && x.toLowerCase().includes(q)
    return hit(opt.label) || hit(opt.sublabel)
  }

  const filteredSections = useMemo(() => {
    return sections
      .map(sec => ({ ...sec, options: (sec.options || []).filter(matchesSearch) }))
      .filter(sec => sec.options.length > 0)
  }, [sections, searchTerm])

  const totalVisible = filteredSections.reduce((n, s) => n + s.options.length, 0)

  const selectedOptions = flatOptions.filter(opt => selectedValues.includes(opt.value))

  const renderTrigger = () => {
    if (selectedOptions.length === 0) {
      return <span style={{ color: '#94a3b8' }}>{placeholder}</span>
    }
    if (!multiple) {
      const opt = selectedOptions[0]
      return (
        <span style={{ fontSize: '13px', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {opt.label}
        </span>
      )
    }
    if (!showTags) {
      return (
        <span style={{ fontSize: '13px', fontWeight: 500 }}>
          {selectedOptions.length} selezionati
        </span>
      )
    }
    if (selectedOptions.length <= 3) {
      return (
        <SelectedTags>
          {selectedOptions.map((o, i) => <Tag key={i}>{o.label}</Tag>)}
        </SelectedTags>
      )
    }
    return <Tag>{selectedOptions.length} selezionati</Tag>
  }

  const selectAllInSection = (sec) => {
    if (!multiple) return
    const sectionValues = sec.options.filter(o => !o.disabled).map(o => o.value)
    const allSelected = sectionValues.every(v => selectedValues.includes(v))
    if (allSelected) {
      onChange && onChange(selectedValues.filter(v => !sectionValues.includes(v)))
    } else {
      const union = new Set([...selectedValues, ...sectionValues])
      onChange && onChange([...union])
    }
  }

  return (
    <Wrapper ref={containerRef} style={style} className={className}>
      {label && <Label>{label}</Label>}
      <Trigger
        ref={triggerRef}
        $isOpen={isOpen}
        $disabled={disabled}
        onClick={handleOpen}
      >
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>{renderTrigger()}</div>
        <Arrow $isOpen={isOpen}>▼</Arrow>
      </Trigger>

      {isOpen && createPortal(
        <OptionsList ref={optionsRef} style={{
          ...(dropdownPos.openAbove
            ? { bottom: dropdownPos.bottom, top: 'auto' }
            : { top: dropdownPos.top, bottom: 'auto' }),
          left: dropdownPos.left,
          width: dropdownPos.width
        }}>
          {searchable && (
            <SearchRow>
              <SearchInput
                ref={searchInputRef}
                type="text"
                placeholder="Cerca..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </SearchRow>
          )}

          {totalVisible === 0 ? (
            <EmptyState>Nessun risultato</EmptyState>
          ) : filteredSections.map((sec, si) => {
            const sectionValues = sec.options.filter(o => !o.disabled).map(o => o.value)
            const selectedInSection = sectionValues.filter(v => selectedValues.includes(v)).length
            const allSelected = sectionValues.length > 0 && selectedInSection === sectionValues.length
            return (
              <div key={sec.label || si}>
                <SectionHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{sec.label}</span>
                    <SectionCount>{sec.options.length}</SectionCount>
                  </div>
                  {multiple && sectionValues.length > 0 && (
                    <SectionAction
                      onClick={e => {
                        e.stopPropagation()
                        selectAllInSection(sec)
                      }}
                    >
                      {allSelected ? 'Deseleziona' : 'Seleziona tutto'}
                    </SectionAction>
                  )}
                </SectionHeader>
                {sec.description && (
                  <SectionDescription>{sec.description}</SectionDescription>
                )}
                {sec.options.map((opt, oi) => {
                  const isSelected = selectedValues.includes(opt.value)
                  return (
                    <Option
                      key={`${opt.value}-${oi}`}
                      $selected={isSelected}
                      $muted={opt.muted}
                      $disabled={opt.disabled}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleValue(opt.value, opt.disabled)
                      }}
                    >
                      {multiple && (
                        <Checkbox
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          tabIndex={-1}
                        />
                      )}
                      <OptionBody>
                        <OptionLabel $muted={opt.muted}>{opt.label}</OptionLabel>
                        {opt.sublabel && (
                          <OptionSublabel>{opt.sublabel}</OptionSublabel>
                        )}
                      </OptionBody>
                      {opt.meta != null && <OptionMeta>{opt.meta}</OptionMeta>}
                    </Option>
                  )
                })}
              </div>
            )
          })}
        </OptionsList>,
        document.body
      )}
    </Wrapper>
  )
}
